import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import axios, { CancelToken } from "axios";
import _, { cloneDeep } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ReactDOM from "react-dom";

// API
import { StatusCode } from "api/protocols";
import {
  AddProductsToVariant,
  IVariantToAttach,
  PostProduct,
  TPostProduct,
  TProductMetaData,
  TPutProduct,
} from "api/rpc/2024-04/facilityAdmin/product/product";
import { GetTaxLine } from "api/rpc/2024-04/facilityAdmin/client/taxLines";
import { GetProductTypes } from "api/rpc/2024-04/facilityAdmin/product/type";
import { GetVendor } from "api/rpc/2024-04/facilityAdmin/product/vendor";
import { GetAccountingReference } from "api/rpc/2024-04/facilityAdmin/facility/accounting";
import { GetDepartments } from "api/rpc/2024-04/facilityAdmin/facility/department";
import { GetProductUnits } from "api/rpc/2024-04/facilityAdmin/product/unit";
import { GetDiscount, IPostDiscount, PostDiscount } from "api/rpc/2024-04/facilityAdmin/product/discount";

// Redux
import { showError, showSuccess } from "redux/actions/ui";
import { TAccountingReference, TTaxLines } from "redux/reducers/models/facility";
import {
  ICategories,
  IDepartmentBase,
  IDepartments,
  ISubcategories,
  TProductType,
  TProductVendor,
  TProductUnit,
  IVariant,
  IProduct,
  TProductRequirements,
  ISalesChannel,
} from "redux/reducers/models/product";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import {
  displayCurrency,
  generateRequiredKeys,
  handleChangeEventInput,
  roundNumber,
  unsavedChangesExist,
  validateRequired,
} from "helpers/Helpers";

// Components
import Page from "components/page/Page";
import Tabs from "components/tabs/Tabs";
import Card from "components/card/Card";
import Input from "components/form/input/Input";
import FormLayout from "components/form/FormLayout";
import { Select } from "components/select";
import TextEditor from "components/textEditor/textEditor";
import { ButtonNew as Button, ButtonNew } from "components/buttonNew";
import Checkbox from "components/form/checkbox/Checkbox";
import TagInput from "components/tagInput/TagInput";
import {
  faChevronDown,
  faChevronUp,
  faMinus,
  faPlus,
  faTrash,
  faQuestionCircle,
  faPen,
  faRotate,
} from "@fortawesome/pro-regular-svg-icons";
import SelectNew from "components/select/SelectNew";
import useModal from "hooks/modals/useModal";
import { GetSalesChannels } from "api/rpc/2024-04/facilityAdmin/facility/salesChannel";
import { GetCustomerType } from "api/rpc/2024-04/facilityAdmin/customer/type";
import Portal from "elements/Portal";
import DataTable from "components/dataTable/DataTable";
import Sheet from "components/sheet/Sheet";
import TimePick from "components/timePick/TimePick";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import ColorPicker from "components/colorpicker/color-picker";
import Toggle from "components/form/toggle/Toggle";
import moment from "moment";
import CopyLink from "components/copyLink/CopyLink";
import { PostProductImage } from "api/rpc/2024-04/facilityAdmin/product/image";
import { GetProductRequirements } from "api/rpc/2024-04/facilityAdmin/client/requirements";
import { Badge } from "components/badge/Badge";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import classNames from "classnames";
import ActionBar from "components/actionBar/ActionBar";
import Callout from "components/callout/Callout";
import { TProductEdit } from "./useEditProduct";
import ImageUpload from "components/imageUpload/imageUpload";
import ProductMembershipOptions from "../product/modals/ProductMembershipOptions";
import AttachProductAsComboModal from "../product/modals/AttachProductAsComboModal";
import { CategoryScale } from "chart.js";

const defaultPostProduct: TPostProduct = {
  title: "",
  subtitle: "",
  type: "",
  description: "",
  internal_description: null,
  taxable: true,
  tax_line_ids: [],
  price: null,
  original_price: null,
  cost: null,
  options: [],
  meta: {
    membership_type: "",
    customer_type_id: -1,
    expiry: null,
  },
  vendor_id: -1,
  accounting_reference_id: -1,
  customer_required: false,
  shipping_required: false,
  fulfillment_required: false,
  track_inventory: false,
  gift_card_restrict_facility: false,
  department_id: -1,
  category_id: -1,
  subcategory_id: -1,
  auto_generate_sku: 0,
  product_unit_id: -1,
  image: [],
  dynamic_combo: false,
  dynamic_combo_quantity: null,
};

interface INewComboVariant {
  product_title: string;
  product_subtitle: string;
  product_id: number;
  id: number;
  variant_title: string;
  price: string;
  cost: string;
  type: string;
  sku: string;
}

interface IComboState {
  comboVariants: INewComboVariant[];
  selectedComboVariantIds: number[];
}

interface IEditComboVariantModalState {
  selectedComboVariant: INewComboVariant;
  priceIsValid: boolean;
  costIsValid: boolean;
}

export default function NewProduct() {
  const { t } = useTranslation();
  const history = useHistory();

  const facilityStore = useAppSelector(store => store.facilityStore);
  const dispatch = useAppDispatch();

  const tabs = [
    {
      id: "overview",
      content: "Overview",
    },
    {
      id: "pricing",
      content: "Pricing & Taxes",
    },
    {
      id: "logistics",
      content: "Logistics",
    },
    {
      id: "variants",
      content: "Variants & Modifiers",
    },
    {
      id: "sales-channels",
      content: "Sales Channels",
    },
    {
      id: "Media",
      content: "Media",
    },
  ];

  // Use States ----------------------
  // State being applied to POST call
  const [product, setProduct] = useState<TPostProduct>(defaultPostProduct);
  const [dirty, setDirty] = useState({
    accounting_reference_id: false,
    category_id: false,
    cost: false,
    department_id: false,
    price: false,
    subcategory_id: false,
    subtitle: false,
    title: false,
    type: false,
    vendor_id: false,
  });
  // Connected to product.meta when creating product
  const [membershipCard, setMembershipCard] = useState<TProductMetaData>({
    membership_type: "",
    customer_type_id: -1,
    expiry: null,
  });

  // Apply UI errors only after Create action
  const [attempted, setAttempted] = useState(false);

  // Tabs
  const [selected, setSelected] = useState(0);
  const [errorTabs, setErrorTabs] = useState<number[]>([]);

  // Overview
  const [departments, setDepartments] = useState<IDepartmentBase[]>([]);
  const [detailsCard, setDetailsCard] = useState({
    availableTaxLines: [] as TTaxLines[],
    allTaxLines: [] as TTaxLines[],
    tax_line_id: null,
    internal_description_visible: false,
  });

  // Pricing & Taxes
  const [markup, setMarkup] = useState<number>(0);
  const [selectedTaxLine, setSelectedTaxLine] = useState<number[]>([]);
  const [hidePriceAfterTax, setHidePriceAfterTax] = useState<boolean>(false);
  const [, setDiscounts] = useState<any[]>([]);
  //const [, setSelectedDiscount] = useState<number[]>([]);
  const [customerTypes, setCustomerTypes] = useState<any[]>([]);
  const [selectedCustomerType, setSelectedCustomerType] = useState<number[]>([]);
  const [salesChannel, setSalesChannel] = useState<(ISalesChannel & { checked?: boolean })[]>([]);
  const [availableAtDate, setAvailableAtDate] = useState<Date | null>(null);
  const [availableUntilDate, setAvailableUntilDate] = useState<Date | null>(null);

  // Logistics
  const [comboState, setComboState] = useState<IComboState>({
    comboVariants: [],
    selectedComboVariantIds: [],
  });

  const {
    state: newComboVariantModal,
    updateModal: updateNewComboVariantModal,
    closeModal: closeNewComboVariantModal,
  } = useModal();

  const {
    state: editComboVariantModal,
    updateModal: updateEditComboVariantModal,
    closeModal: closeEditComboVariantModal,
  } = useModal<IEditComboVariantModalState>({
    selectedComboVariant: null,
    priceIsValid: false,
    costIsValid: false,
    clearOnClose: true,
  });

  const [logisticsCard, setLogisticsCard] = useState({
    types: [] as TProductType[],
    vendors: [] as TProductVendor[],
    accountingReferences: [] as TAccountingReference[],
    productUnits: [] as TProductUnit[],
    productTypeSearch: "",
    accountingRefSearch: "",
    vendorSearch: "",
    productUnitSearch: "",
  });

  // Variants & Modifiers
  const [variantsCard, setVariantsCard] = useState({
    multipleOptions: false,
  });

  // Sales Channels

  // Media
  const [imageURL, setImageURL] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);

  // Use Memo ----------------------
  // Visibility AND filter dependant on selected product.type
  const membershipApplicationFilter =
    product.type?.toLocaleLowerCase() == "membership"
      ? "green_fee"
      : product.type?.toLocaleLowerCase() == "membership add on"
      ? "power_cart"
      : null;

  const comboSelected = product.type?.toLocaleLowerCase() == "combo";
  const regularComboSelected = comboSelected && !product.dynamic_combo;
  //const dynamicComboSelected = comboSelected && product.dynamic_combo;

  function isValidCurrency(amount: string) {
    return amount != null && amount != "" && !isNaN(Number(amount));
  }

  const comboPriceOverride = useMemo(() => {
    if (regularComboSelected) {
      if (variantsCard.multipleOptions || !comboState.comboVariants) {
        return 0;
      } else {
        return comboState.comboVariants.reduce(
          (accumulator, next) => accumulator + (isValidCurrency(next.price) ? Number(next.price) : 0),
          0,
        );
      }
    } else {
      return null;
    }
  }, [regularComboSelected, variantsCard.multipleOptions, comboState.comboVariants]);

  const comboCostOverride = useMemo(() => {
    if (regularComboSelected) {
      if (variantsCard.multipleOptions || !comboState.comboVariants) {
        return 0;
      } else {
        return comboState.comboVariants.reduce(
          (accumulator, next) => accumulator + (isValidCurrency(next.cost) ? Number(next.cost) : 0),
          0,
        );
      }
    } else {
      return null;
    }
  }, [regularComboSelected, variantsCard.multipleOptions, comboState.comboVariants]);

  const allComboProductsSelected = useMemo(() => {
    if (comboState.comboVariants == null || comboState.comboVariants.length === 0) {
      return false;
    } else {
      return comboState.comboVariants.every(comboVariant =>
        comboState.selectedComboVariantIds.includes(comboVariant.id),
      );
    }
  }, [comboState.comboVariants, comboState.selectedComboVariantIds]);

  // memoized required keys of an IProduct.  Determined by facilityStore,validationNewProduct.required if found
  const requiredKeys = useMemo<any[]>(() => {
    if (
      facilityStore.validationNewProduct.required !== undefined &&
      typeof facilityStore.validationNewProduct.required !== "string"
    ) {
      const rest = facilityStore?.validationNewProduct.required as Omit<
        TProductRequirements,
        "client_id" | "created_at" | "deleted_at" | "id" | "updated_at"
      >;

      return generateRequiredKeys<TPostProduct>(rest as Record<string, boolean>, "product_");
    }
    return [];
  }, [facilityStore.validationNewProduct.required]);

  // Use Modals ----------------------
  const {
    state: discountModal,
    updateModal: updateDiscountModal,
    closeModal: closeDiscountModal,
  } = useModal({
    title: "",
    application: "order", // Temporary value until I know what this is
    scheduledDiscount: false,
    value_type: "percent",
    value: 0,
    customerType: [],
    has_customer: false,
    start_date: new Date(),
    end_date: new Date(),
    dateRange: [],
    startTime: null,
    endTime: null,
    salesChannel: 0,
    custom: false,
    tracked: false,
  });

  // Use Effects ----------------------
  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadTaxLines(source.token);
    void loadProductTypes(source.token);
    void loadVendors(source.token);
    void loadDepartments(source.token);
    void loadAccountingReferences(source.token);
    void loadProductUnits(source.token);
    void loadCustomerTypes();
    void loadSalesChannels();
    void loadDiscounts();
    return () => source.cancel();
  }, []);

  /* keep membership card props and product POST params in sync */
  useEffect(() => {
    if (unsavedChangesExist(membershipCard, product.meta)) {
      setProduct(prev => ({ ...prev, meta: { ...prev.meta, ...membershipCard } }));
    }
  }, [membershipCard]);

  useEffect(() => {
    if (!dirty.title && !dirty.subtitle && !dirty.department_id && !dirty.category_id && !dirty.subcategory_id) {
      setErrorTabs(prev => prev.filter(tab => tab !== 0));
    }
    if (!dirty.price && !dirty.cost) {
      setErrorTabs(prev => prev.filter(tab => tab !== 1));
    }
    if (!dirty.type && !dirty.vendor_id && !dirty.accounting_reference_id) {
      setErrorTabs(prev => prev.filter(tab => tab !== 2));
    }
  }, [dirty]);

  // Startup API Calls ----------------------
  async function loadTaxLines(token: CancelToken) {
    const taxLineRes = await GetTaxLine(null, false);

    if (token && token.reason) {
      return;
    }
    if (taxLineRes.status !== StatusCode.OK) {
      return;
    }

    setDetailsCard(prev => ({ ...prev, availableTaxLines: taxLineRes.data, allTaxLines: taxLineRes.data }));

    const requirementsRes = await GetProductRequirements(null, true, token);
    console.log("Requirements", requirementsRes.data);
  }

  async function loadProductTypes(token: CancelToken) {
    const res = await GetProductTypes(null, false, token);

    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      return;
    }

    const normalizedTypes = [{ id: -1, client_id: null as number, title: "", locked: true }, ...res.data];

    setLogisticsCard(prev => ({ ...prev, types: normalizedTypes }));
  }

  async function loadVendors(token: CancelToken) {
    const res = await GetVendor(null, false, token);

    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      return;
    }

    const normalizedVendors = [{ id: -1, client_id: null as number, title: "None" }, ...res.data];

    setLogisticsCard(prev => ({ ...prev, vendors: normalizedVendors }));
  }

  async function loadDepartments(token: CancelToken) {
    const res = await GetDepartments({ extended: true }, token ? false : true, token);

    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      return;
    }

    setDepartments(res.data);
  }

  async function loadAccountingReferences(token: CancelToken) {
    const res = await GetAccountingReference(null, false, token);

    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      return;
    }

    const normalizedReferences: TAccountingReference[] = [
      { id: -1, client_id: null as number, title: "None", reference_number: null as string },
      ...res.data,
    ];

    setLogisticsCard(prev => ({ ...prev, accountingReferences: normalizedReferences }));
  }

  async function loadProductUnits(token: CancelToken) {
    const unitRes = await GetProductUnits(null, false, token);

    if (token && token.reason) {
      return;
    }
    if (unitRes.status !== StatusCode.OK) {
      return;
    }

    const normalizedUnits = [{ id: -1, client_id: null as number, name: "None" }, ...unitRes.data];

    setLogisticsCard(prev => ({ ...prev, productUnits: normalizedUnits }));
  }

  async function loadCustomerTypes() {
    const res = await GetCustomerType(
      {
        booking_category: true,
      },
      false,
    );
    if (res.status !== StatusCode.OK) {
      return;
    }
    setCustomerTypes(res.data);
  }

  async function loadSalesChannels() {
    const res = await GetSalesChannels();

    if (res.status !== StatusCode.OK) {
      return;
    }

    if (salesChannel.every(channel => channel.checked === null)) {
      if (Array.isArray(res.data)) {
        res.data.forEach((channel: ISalesChannel & { checked: boolean }) => {
          channel.checked = false;
        });
      }
    }

    setSalesChannel(Array.isArray(res.data) ? res.data : [res.data]);
    console.log("Sales Channel", res.data);
  }

  async function loadDiscounts() {
    const res = await GetDiscount();

    if (res.status !== StatusCode.OK) {
      return;
    }

    setDiscounts(Array.isArray(res.data) ? res.data : [res.data]);
  }

  // Triggered API Calls ----------------------
  async function createProduct() {
    // Get client product requirements and check against product
    const requiredRes = await GetProductRequirements(null, true);
    if (requiredRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.product.product_new.056")));
      return;
    }
    if (!checkClientRequirements(requiredRes.data as TProductRequirements)) {
      dispatch(showError(t("Missing Required Fields")));
      return;
    }

    const validated = validateRequired(
      product as TProductEdit,
      requiredKeys as Array<keyof TProductEdit | "cost_with_track_inventory">,
    );

    if (!validated) {
      setAttempted(true); // 'Toggles' error styling
      dispatch(showError(t("secure.facility.product.product_new.056")));
      return;
    }

    const params = removeEmpty(product, variantsCard.multipleOptions);

    if (comboSelected && (!product.dynamic_combo || variantsCard.multipleOptions)) {
      params.price = comboPriceOverride;
      params.cost = comboCostOverride;
    }

    const postProductRes = await PostProduct(params, true);

    if (postProductRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.product.product_new.057")));
      return;
    }

    if (imageFile) {
      const formData = new FormData();
      formData.append("image", imageFile);
      formData.append("product_id", String(postProductRes.data.id));

      const imageRes = await PostProductImage(formData, true);

      if (imageRes.status !== StatusCode.OK) {
        dispatch(showError(t("secure.facility.product.product_new.058")));
        return;
      }
    }

    if (comboSelected && comboState.comboVariants.length > 0 && !variantsCard.multipleOptions) {
      const variants_to_attach: IVariantToAttach[] = comboState.comboVariants.map(comboVariant => {
        return {
          variant_id: comboVariant.id,
          price: Number(comboVariant.price),
          cost: Number(comboVariant.cost),
        };
      });

      const addComboRes = await AddProductsToVariant(
        { base_variant_id: postProductRes.data.variants[0].id, variants_to_attach },
        true,
      );

      if (addComboRes.status !== StatusCode.OK) {
        dispatch(showError(t("secure.facility.product.new_product.001")));
      }
    }
    // navigate to edit product after success
    history.push("/admin/product/" + String(postProductRes?.data?.id));
  }

  async function addDiscount(discount: any) {
    if (discount.scheduledDiscount) {
      if (discount.start_date === null || discountModal.end_date === null) {
        dispatch(showError("Start and end date are required"));
        return;
      }
      if (discount.startTime > discount.endTime) {
        dispatch(showError("Start time must be before end time"));
        return;
      }
    }

    selectedCustomerType.forEach((customerType: any) => {
      discount.customerType.push(customerType.id);
    });

    // if (discount.title === "" ||
    //   discount.value_type === null ||
    //   discount.value === 0 ||
    //   discount.customerType.length === 0 ||
    //   discount.salesChannel === 0
    // ) {
    //   dispatch(showError("All fields are required"));
    //   console.log("Title", discount.title);
    //   console.log("Value Type", discount.value_type);
    //   console.log("Value", discount.value);
    //   console.log("Customer Type", discount.customerType);
    //   console.log("Sales Channel", discount.salesChannel);
    //   return;
    // }

    const iDiscount: IPostDiscount = {
      title: discount.title,
      application: discount.application,
      value_type: "percent",
      value: discount.value,
      custom: discount.custom,
      tracked: false,
    };
    const res = await PostDiscount(iDiscount, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error adding discount")));
      return;
    }

    dispatch(showSuccess(t("Discount added successfully")));

    void loadDiscounts();
  }

  // Handlers ----------------------
  const handleTabChange = (selectedTabIndex: number) => {
    setSelected(selectedTabIndex);
  };

  /**
   * Validate specific key param.
   * - Used in JSX to help validate specific error styling
   */
  function validated(key: keyof TPutProduct | "cost_with_track_inventory") {
    if (requiredKeys.length === 0) {
      return true;
    }

    if ((requiredKeys as unknown as [keyof TPutProduct | "cost_with_track_inventory"]).includes(key)) {
      return validateRequired(product as TProductEdit, [key]);
    }
    return true;
  }
  function checkClientRequirements(requirements: TProductRequirements) {
    const vals = [
      { id: 0, name: "accounting_reference_id", tab: 2 },
      { id: 1, name: "category_id", tab: 0 },
      { id: 2, name: "cost", tab: 1 },
      { id: 3, name: "department_id", tab: 0 },
      { id: 4, name: "price", tab: 1 },
      { id: 5, name: "subcategory_id", tab: 0 },
      { id: 6, name: "subtitle", tab: 0 },
      { id: 7, name: "title", tab: 0 },
      { id: 8, name: "type", tab: 2 },
      { id: 9, name: "vendor_id", tab: 2 },
    ];
    let passed = true;

    for (let i = 0; i < vals.length; i++) {
      const propName = vals[i].name as keyof TPostProduct;
      const val = `product_${propName}` as keyof TProductRequirements;
      const tab = vals[i].tab;

      console.log("Checking", propName, "with value", product[propName], "and requirements", requirements[val]);

      if (
        (requirements[val] === true || requirements[val] === 1) &&
        (product[propName] === -1 || product[propName] === "" || product[propName] === null)
      ) {
        setErrorTabs(prev => [...prev, tab]);
        setDirty(prev => ({ ...prev, [propName]: true }));
        passed = false;
      }
    }
    return passed;
  }

  // Overview

  // Pricing & Taxes
  const toggleTaxLines = (value: number) => {
    setSelectedTaxLine(prev => (prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]));
  };

  // const toggleDiscounts = (value: number) => {
  //   setSelectedDiscount((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  // };

  const toggleCustomerType = (value: number) => {
    setSelectedCustomerType(prev => (prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]));
  };

  function handleCustomerTypeCheckbox(event: ChangeEvent<HTMLInputElement>, index: number) {
    const { checked } = event.target;
    const newDiscounts = [...discountModal.customerType];
    newDiscounts[index] = { ...newDiscounts[index], checked: checked };
    console.log("Discounts", newDiscounts);
    updateDiscountModal({ customerType: newDiscounts });
  }

  function handleAllCustomerTypeCheckbox(event: ChangeEvent<HTMLInputElement>) {
    const { checked } = event.target;
    const newDiscounts = [...discountModal.customerType];
    for (let i = 0; i < newDiscounts.length; i++) {
      newDiscounts[i] = { ...newDiscounts[i], has_customer: checked };
    }
    console.log("newDiscounts", newDiscounts);
    updateDiscountModal({ customerType: newDiscounts });
  }

  function getPriceAfterTax() {
    const originalPrice = product?.price ?? 0;
    let price = product?.price ?? 0;
    for (let i = 0; i < selectedTaxLine.length; i++) {
      price += originalPrice * detailsCard.availableTaxLines.find(tax => tax.id === selectedTaxLine[i])?.tax;
    }
    return roundNumber(price, 2);
  }

  function getTaxNames() {
    const taxNames = [];
    console.log("Selected Tax Lines", selectedTaxLine);
    console.log("Available Tax Lines", detailsCard.availableTaxLines);
    for (let i = 0; i < selectedTaxLine.length; i++) {
      taxNames[i] = detailsCard.availableTaxLines.find(tax => tax.id === selectedTaxLine[i])?.name ?? "";
    }
    return taxNames.join(", ");
  }

  function setDateRangeStart(date: Date) {
    updateDiscountModal({ start_date: date });
  }

  function setDateRangeEnd(date: Date) {
    updateDiscountModal({ end_date: date });
  }

  // Logistics
  function handleProductTypeSearch(search: string) {
    setLogisticsCard(prev => ({ ...prev, productTypeSearch: search }));
    if (search.length === 0) {
      setProduct(prev => ({
        ...prev,
        type: "",
      }));
    }
  }

  function handleAccountingRefSearch(search: string) {
    setLogisticsCard(prev => ({ ...prev, accountingRefSearch: search }));
    if (search.length === 0) {
      setProduct(prev => ({ ...prev, accounting_reference_id: -1 }));
    }
  }

  function handleVendorSearch(search: string) {
    setLogisticsCard(prev => ({ ...prev, vendorSearch: search }));
    if (search.length === 0) {
      setProduct(prev => ({ ...prev, vendor_id: null }));
    }
  }

  function handleProductUnitSearch(search: string) {
    setLogisticsCard(prev => ({ ...prev, productUnitSearch: search }));
    if (search.length === 0) {
      setProduct(prev => ({ ...prev, product_unit_id: null }));
    }
  }

  function handleSelectProductType(id: number) {
    if (logisticsCard.types.find(type => type.id === id)?.title === "Modifier") {
      setVariantsCard(prevState => ({ ...prevState, multipleOptions: false }));
      setProduct(prevState => ({ ...prevState, options: [] }));
    }

    setProduct(prev => ({
      ...prev,
      type: id === -1 ? "" : logisticsCard.types.find(type => type.id === id)?.title,
    }));
  }

  function generateSKU(): string {
    let sku = "";
    if (product?.title) {
      const titleWords = product.title.split(" ");
      const name = titleWords.map(word => word.charAt(0).toUpperCase()).join("");
      sku = name + "-";
    }
    return sku + Math.floor(Math.random() * 10000).toString();
  }

  // Variants & Modifiers
  /** Resets product.options when toggled TRUE */
  function toggleMultipleOptions(e: ChangeEvent<HTMLInputElement>) {
    setProduct(prev => ({ ...prev, options: e.target.checked ? [{ name: "", values: [""] }] : [] }));
    handleChangeEventInput(e, variantsCard, setVariantsCard);
  }

  function handleOptionChange(e: ChangeEvent<HTMLInputElement>) {
    const { id, value } = e.target;

    // handle tag input -- deep object value -- Ignore ',' from keyboard
    if (value !== ",") {
      const tempState = { ...product };
      _.set(tempState, id, value);
      setProduct(() => ({ ...tempState }));
    }
  }

  function handleAddOptionTag(e: React.KeyboardEvent<HTMLInputElement>) {
    const {
      key,
      currentTarget: { id },
    } = e;

    const regex = /^(?!\s,*$).+/g;
    const tempState = { ...product };
    const arrayId = id.substring(0, id.length - 3);

    if (key === "Enter" || key === ",") {
      if (_.get(tempState, id).match(regex) !== null) {
        _.invoke(tempState, `${arrayId}.push`, "");
        setProduct(prev => ({ ...prev, ...tempState }));
      }
    }
  }

  function handleRemoveOptionTag(optionIndex: number, tagIndex: number) {
    const tempState = { ...product };
    tempState.options[optionIndex].values.splice(tagIndex, 1);
    setProduct(prev => ({ ...prev, ...tempState }));
  }

  // function renderChargeTaxes(): ReactNode[] {
  //   return [
  //     <Checkbox
  //       key={1}
  //       id="taxable"
  //       size="medium"
  //       value={product.taxable}
  //       checked={Boolean(product?.taxable)}
  //       onChange={(e) => setProduct((prev) => ({ ...prev, taxable: e.target.checked }))}
  //       label={t("secure.facility.product.product_new.017")}
  //     />,

  //     <div key={2} style={{ marginLeft: "-2rem", marginTop: "2rem" }}>
  //       <React.Fragment>
  //         {product.taxable ? (
  //           <div className="edit-tax-lines_single_variant_container">
  //             <TagInputSelect
  //               tags={product.tax_line_ids.map((taxLine, index: number) => {
  //                 const line = detailsCard.allTaxLines.find((val) => val.id === taxLine);
  //                 return { name: `${line.name} (${line.percentage}%)`, id: line.id };
  //               })}
  //               selectOptions={detailsCard.availableTaxLines?.map((line) => {
  //                 return {
  //                   name: `${line.name} (${line.percentage}%)`,
  //                   id: line.id,
  //                 };
  //               })}
  //               onChange={(value) => {
  //                 handleTaxDropdown(Number(value));
  //               }}
  //               onTagClick={(taxLineId) => handleRemoveTaxLine(taxLineId)}
  //               className="edit-tax-lines_single_variant_container_tag_input"
  //               helpText={t("secure.facility.product.product_new.058")}
  //             />
  //           </div>
  //         ) : null}
  //       </React.Fragment>
  //     </div>,
  //   ];
  // }

  function removeVariantOption(index: number) {
    const tempProduct = { ...product };
    tempProduct.options?.splice(index, 1);
    ReactDOM.unstable_batchedUpdates(() => {
      if (tempProduct?.options?.length === 0) {
        setVariantsCard({ multipleOptions: false });
      }
      setProduct(prevState => ({ ...prevState, ...tempProduct }));
    });
  }

  // Sales Channels
  function handleTimeAvailability(id: "available_on_time" | "unavailable_on_time", value: string) {
    if (value === undefined) {
      setProduct(prev => ({ ...prev, [id]: null }));
      return;
    }

    if (value === null || value?.length === 0) {
      handleTimeClear(id);
      return;
    }

    if (product.available_on.toString() === product.unavailable_on.toString()) {
      const dateVal = id === "available_on_time" ? "available_on" : "unavailable_on";
      const dateValue = new Date(`${product[dateVal].toString()} ${value}`);

      if (id === "available_on_time") {
        const unavailableDate = new Date(product.unavailable_on.toString() + " " + product.unavailable_on_time);
        if (dateValue < unavailableDate) {
          setProduct(prev => ({ ...prev, available_on_time: value }));
        }
      }
      if (id === "unavailable_on_time") {
        const availableDate = new Date(product.available_on.toString() + " " + product.available_on_time);
        if (dateValue > availableDate) {
          setProduct(prev => ({ ...prev, unavailable_on_time: value }));
        }
      }

      return;
    }

    setProduct(prev => ({ ...prev, [id]: value }));
    // setProduct(prev => ({
    //   ...prev,
    //   type: id === null ? "" : logisticsCard.types.find(type => type.id === id)?.title,
    // }));

    setComboState(prev => ({ ...prev, selectedComboVariantIds: [] }));
  }

  function handleAvailableOnDateSelectorChange(selectedDate: Date) {
    const tempDate = new Date(selectedDate);

    // Handle range error with available_at and available_until
    if (availableUntilDate) {
      const tempStart = new Date(selectedDate.toLocaleDateString() + " " + product.available_on_time);
      const tempEnd = new Date(availableUntilDate.toLocaleDateString() + " " + product.unavailable_on_time);

      if (tempStart > tempEnd) {
        const monthAfter = new Date(selectedDate.getTime());
        monthAfter.setMonth(monthAfter.getMonth() + 1);

        setProduct(prev => ({ ...prev, unavailable_on: monthAfter }));
        setAvailableUntilDate(monthAfter);
      }
    }

    setProduct(prev => ({ ...prev, available_on: tempDate }));
    setAvailableAtDate(selectedDate);
  }

  function handleAvailableUntilDateSelectorChange(selectedDate: Date) {
    const tempDate = new Date(selectedDate);

    setAvailableUntilDate(selectedDate);
    setProduct(prev => ({ ...prev, available_on: tempDate }));
  }

  function handleTimeClear(id: "available_on_time" | "unavailable_on_time") {
    if (id === "available_on_time") {
      setProduct(prevState => ({
        ...prevState,
        available_on: null,
        available_on_time: null,
      }));

      return;
    }
    if (id === "unavailable_on_time") {
      setProduct(prevState => ({
        ...prevState,
        unavailable_on: null,
        unavailable_on_time: null,
      }));
      return;
    }
  }

  function handleAvailableOnToggle(e: ChangeEvent<HTMLInputElement>) {
    const { checked } = e.target;

    if (!checked) {
      setAvailableAtDate(null);
      setProduct(prev => ({ ...prev, available_on: null, available_on_time: null }));
    } else {
      // re-apply previous date when toggled on
      if (product.available_on) {
        setProduct(prev => ({
          ...prev,
          available_on: product.available_on,
          available_on_time: product.available_on_time,
        }));
        setAvailableAtDate(new Date(product.available_on));
      } else {
        const newDate = new Date();
        newDate.setHours(0, 0, 0, 0);

        setProduct(prev => ({
          ...prev,
          available_on: newDate,
          available_on_time: "12:00",
        }));

        setAvailableAtDate(newDate);
      }
    }
  }

  function handleAvailableUntilToggle(e: ChangeEvent<HTMLInputElement>) {
    const { checked } = e.target;

    if (!checked) {
      setAvailableUntilDate(null);
      setProduct(prev => ({ ...prev, unavailable_on: null, unavailable_on_time: null }));
    } else {
      // try to re-apply previous date when toggled on
      if (product.unavailable_on) {
        setProduct(prev => ({
          ...prev,
          unavailable_on: product.unavailable_on,
          unavailable_on_time: product.unavailable_on_time,
        }));
        setAvailableUntilDate(new Date(product.unavailable_on));
      } else {
        const newDate = new Date();
        newDate.setHours(0, 0, 0, 0);
        newDate.setDate(newDate.getDate() + 7); // 1 Week after todays date

        setProduct(prev => ({
          ...prev,
          unavailable_on: newDate,
          unavailable_on_time: "12:00",
        }));

        setAvailableUntilDate(newDate);
      }
    }
  }

  function handleMarkupChange(value: number) {
    if (!product?.cost) {
      showError("Cost is required");
    } else {
      setMarkup(value);
      setProduct(prev => ({
        ...prev,
        price: Number(roundNumber(product.cost * (value / 100) + product.cost, 2)),
      }));
    }
  }

  function handleCostChange(value: number) {
    if (value === 0) {
      setMarkup(0);
      setProduct(prev => ({
        ...prev,
        cost: Number(roundNumber(value, 2)),
      }));
      return;
    }
    const price = Number(product?.price ?? 0);
    setMarkup(((price - value) / value) * 100);
    setProduct(prev => ({
      ...prev,
      cost: Number(roundNumber(value, 2)),
    }));
  }

  function handlePriceChange(value: number) {
    const cost = Number(product?.cost ?? 0);
    setMarkup(((value - cost) / cost) * 100);
    setProduct(prev => ({
      ...prev,
      price: Number(roundNumber(value, 2)),
    }));
  }

  // Media

  const primaryAction = {
    content: t("secure.facility.product.product_new.001"),
    action: () => createProduct(),
  };
  function handleNewComboVariantModalOk(variant: IVariant, product: IProduct) {
    setComboState(prev => ({
      ...prev,
      comboVariants: [
        ...prev.comboVariants,
        {
          product_title: product.title,
          product_subtitle: product.subtitle,
          product_id: variant.product_id,
          id: variant.id,
          variant_title: variant.title,
          price: variant.price == null ? "" : String(variant.price),
          cost: variant.cost == null ? "" : String(variant.cost),
          type: product.type,
          sku: variant.sku,
        },
      ],
    }));
  }

  function handleRemoveComboVariant(comboVariantId: number) {
    setComboState(prev => ({
      ...prev,
      comboVariants: prev.comboVariants.filter(comboVariant => comboVariant.id !== comboVariantId),
      selectedComboVariantIds: prev.selectedComboVariantIds.filter(
        selectedComboVariantId => selectedComboVariantId !== comboVariantId,
      ),
    }));
  }

  function handleComboVariantsRemoval() {
    setComboState(prev => ({
      ...prev,
      comboVariants: prev.comboVariants.filter(
        comboVariant => !prev.selectedComboVariantIds.some(selectedId => selectedId === comboVariant.id),
      ),
      selectedComboVariantIds: [],
    }));
  }

  function handleUpdateComboVariant(comboVariant: INewComboVariant) {
    if (product.dynamic_combo || comboVariant == null) {
      return;
    }

    updateEditComboVariantModal({
      isOpen: true,
      selectedComboVariant: cloneDeep(comboVariant),
      priceIsValid: isValidCurrency(comboVariant.price),
      costIsValid: isValidCurrency(comboVariant.cost),
    });
  }

  function handleEditComboVariantPrice(price: string) {
    updateEditComboVariantModal({
      selectedComboVariant: {
        ...editComboVariantModal.selectedComboVariant,
        price,
      },
      priceIsValid: isValidCurrency(price),
    });
  }

  function handleEditComboVariantCost(cost: string) {
    updateEditComboVariantModal({
      selectedComboVariant: {
        ...editComboVariantModal.selectedComboVariant,
        cost,
      },
      costIsValid: isValidCurrency(cost),
    });
  }

  function handleEditComboVariantModalOk() {
    setComboState(prev => ({
      ...prev,
      comboVariants: prev.comboVariants.map(comboVariant => {
        if (comboVariant.id === editComboVariantModal.selectedComboVariant?.id) {
          return {
            ...comboVariant,
            cost: editComboVariantModal.costIsValid
              ? editComboVariantModal.selectedComboVariant.cost
              : comboVariant.cost,
            price: editComboVariantModal.priceIsValid
              ? editComboVariantModal.selectedComboVariant.price
              : comboVariant.price,
          };
        } else {
          return comboVariant;
        }
      }),
    }));

    closeEditComboVariantModal();
  }

  function handleSelectComboVariant(comboVariantId: number) {
    if (comboState.selectedComboVariantIds.includes(comboVariantId)) {
      setComboState(prev => ({
        ...prev,
        selectedComboVariantIds: prev.selectedComboVariantIds.filter(
          selectedVariantId => selectedVariantId !== comboVariantId,
        ),
      }));
    } else {
      setComboState(prev => ({
        ...prev,
        selectedComboVariantIds: [...prev.selectedComboVariantIds, comboVariantId],
      }));
    }
  }

  function handleSelectAllComboVariants() {
    if (comboState.comboVariants.length === 0) {
      return;
    } else if (allComboProductsSelected) {
      setComboState(prev => ({ ...prev, selectedComboVariantIds: [] }));
    } else {
      setComboState(prev => ({
        ...prev,
        selectedComboVariantIds: prev.comboVariants.map(comboVariant => comboVariant.id),
      }));
    }
  }

  function handleClearSelectedComboVariants() {
    setComboState(prev => ({ ...prev, selectedComboVariantIds: [] }));
  }

  function renderComboCard() {
    const multipleOptions = variantsCard.multipleOptions;

    if (comboSelected) {
      return (
        <Card
          title={
            <div className="new-product-combo-title-container">
              <div>{t("secure.facility.product.new_product.002")}</div>
              {!multipleOptions && (
                <Badge type="primary">
                  {`${comboState.comboVariants?.length} ${
                    comboState.comboVariants?.length === 1
                      ? t("secure.facility.product.new_product.003")
                      : t("secure.facility.product.new_product.004")
                  }`}
                </Badge>
              )}
            </div>
          }
          subtitle={t("secure.facility.product.new_product.005")}
          titleActions={
            !multipleOptions
              ? [
                  {
                    action: () => updateNewComboVariantModal({ isOpen: true }),
                    content: (
                      <>
                        <FontAwesomeIcon size="lg" className="mr-2" icon={faPlus} />{" "}
                        {t("secure.facility.product.new_product.006")}
                      </>
                    ),
                    buttonType: "text",
                  },
                ]
              : []
          }
        >
          <Card.Section>
            <Checkbox
              size="small"
              label={t("secure.facility.product.new_product.007")}
              checked={product.dynamic_combo}
              onChange={() => setProduct(prev => ({ ...prev, dynamic_combo: !prev.dynamic_combo }))}
              disabled={multipleOptions}
            />

            {multipleOptions && (
              <div className="mt-3">
                <Callout
                  type="info"
                  title={t("secure.facility.product.new_product.023")}
                  content={t("secure.facility.product.new_product.024")}
                />
              </div>
            )}
          </Card.Section>

          {!multipleOptions && (
            <>
              {product.dynamic_combo && (
                <Card.Section>
                  <div className="flex flex-row" style={{ display: "flex", gap: "20px" }}>
                    <Input
                      id="dynamic_combo_quantity"
                      value={product.dynamic_combo_quantity || ""}
                      label={t("secure.facility.product.new_product.008")}
                      onChange={e => handleChangeEventInput(e, product, setProduct)}
                    />
                    <Input
                      value={product.price || ""}
                      prefix="$"
                      label={t("secure.facility.product.product_new.013")}
                      onChange={e => setProduct(prev => ({ ...prev, price: e.target.value as unknown as number }))}
                    />
                    <Input
                      value={product.cost || ""}
                      prefix="$"
                      label={t("secure.facility.product.product_new.015")}
                      onChange={e => setProduct(prev => ({ ...prev, cost: e.target.value as unknown as number }))}
                    />
                  </div>
                </Card.Section>
              )}

              <ActionBar
                visible={comboState.selectedComboVariantIds.length > 0}
                selectedItemIds={comboState.selectedComboVariantIds}
                clearSelectedItemIds={handleClearSelectedComboVariants}
                itemSingularTitle={t("secure.facility.product.new_product.009")}
                itemPluralTitle={t("secure.facility.product.new_product.010")}
                primaryActions={[
                  {
                    icon: faTrash,
                    title: t("secure.facility.product.new_product.011"),
                    action: handleComboVariantsRemoval,
                  },
                ]}
              >
                <DataTable
                  columns={[
                    {
                      label: "",
                      width: "20px",
                      content: (
                        <Checkbox
                          labelHidden
                          size="small"
                          checked={allComboProductsSelected}
                          onChange={handleSelectAllComboVariants}
                        />
                      ),
                    },
                    { label: t("secure.facility.product.new_product.012") },
                    { label: t("secure.facility.product.new_product.013") },
                    { label: t("secure.facility.product.new_product.014") },
                    { label: t("secure.facility.product.new_product.015") },
                    { label: t("secure.facility.product.new_product.016") },
                    {
                      label: t("secure.facility.product.new_product.017"),
                      content: (
                        <div className="table-label new-product-combo-sku-heading">
                          <span>{t("secure.facility.product.new_product.018")}</span>
                          <OverlayTrigger
                            placement="bottom"
                            overlay={
                              <Tooltip id="new-product-combo-sku-tooltip">{`${t(
                                "secure.facility.product.new_product.019",
                              )}`}</Tooltip>
                            }
                          >
                            <FontAwesomeIcon className="new-product-combo-sku-tooltip-icon" icon={faQuestionCircle} />
                          </OverlayTrigger>
                        </div>
                      ),
                    },
                    { label: t("secure.facility.product.new_product.020") },
                  ]}
                >
                  {comboState.comboVariants?.map(comboVariant => {
                    return (
                      <tr key={comboVariant.id}>
                        <td>
                          <Checkbox
                            size="small"
                            labelHidden
                            checked={comboState.selectedComboVariantIds.includes(comboVariant.id)}
                            onChange={() => handleSelectComboVariant(comboVariant.id)}
                          />
                        </td>
                        <td>
                          <div>
                            <div className="new-product-combo-variant-title">{comboVariant.product_title}</div>
                            {comboVariant.product_subtitle && (
                              <div className="new-product-combo-variant-subtitle">{comboVariant.product_subtitle}</div>
                            )}
                          </div>
                        </td>
                        <td>
                          {comboVariant.variant_title?.length > 0 &&
                          comboVariant.variant_title !== comboVariant.product_title
                            ? comboVariant.variant_title
                            : ""}
                        </td>
                        <td>{comboVariant.price ? displayCurrency("cad", parseFloat(comboVariant.price)) : ""}</td>
                        <td>{comboVariant.cost ? displayCurrency("cad", parseFloat(comboVariant.cost)) : ""}</td>
                        <td>
                          <Badge>{comboVariant.type}</Badge>
                        </td>
                        <td>{comboVariant.sku}</td>
                        <td>
                          <div className="new-product-combo-actions">
                            <FontAwesomeIcon
                              className="new-product-combo-action"
                              icon={faTrash}
                              onClick={() => handleRemoveComboVariant(comboVariant.id)}
                            />

                            <FontAwesomeIcon
                              className={classNames("new-product-combo-action", {
                                "combo-action-disabled": product.dynamic_combo,
                              })}
                              icon={faPen}
                              onClick={() => handleUpdateComboVariant(comboVariant)}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </DataTable>
              </ActionBar>
            </>
          )}
        </Card>
      );
    } else {
      return <></>;
    }
  }

  return (
    <Page
      title={t("secure.facility.product.product_new.004")}
      primaryAction={primaryAction}
      breadcrumbs={[{ label: t("secure.facility.product.product_new.005"), url: "/admin/product" }]}
    >
      <Page.Section>
        <div style={{ display: "flex", overflowX: "auto" }}>
          <Tabs tabs={tabs} selected={selected} onSelect={handleTabChange} errorTabs={errorTabs} />
        </div>
      </Page.Section>
      <br />

      {/* Overview */}
      {selected === 0 ? (
        <Card title={t("secure.facility.product.product_new.006")}>
          <Card.Section>
            <FormLayout>
              <FormLayout.Group>
                <Input
                  id="title.data"
                  value={product?.title || ""}
                  label={t("secure.facility.product.product_new.007")}
                  onChange={e => {
                    setProduct(prev => ({
                      ...prev,
                      title: e.target.value,
                    }));
                    setDirty(prev => ({ ...prev, title: false }));
                  }}
                  placeholder={t("secure.facility.product.product_new.008")}
                  error={(!validated("title") && attempted) || dirty.title}
                />
                <Input
                  id="subtitle"
                  value={product?.subtitle || ""}
                  label={t("secure.facility.product.product_new.009")}
                  onChange={e => {
                    handleChangeEventInput(e, product, setProduct);
                    setDirty(prev => ({ ...prev, subtitle: false }));
                  }}
                  placeholder={t("secure.facility.product.product_new.010")}
                  error={(!validated("subtitle") && attempted) || dirty.subtitle}
                />
              </FormLayout.Group>

              <FormLayout.Group>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <TextEditor
                    markdownText={product?.description || ""}
                    markdownTextOnChange={(description: string) => setProduct(prev => ({ ...prev, description }))}
                    label={t("secure.facility.product.product.012")}
                  />
                  {/* <div className="product-description-subtext">This is a hint text to help the user.</div> */}
                </div>
              </FormLayout.Group>

              <FormLayout.Group>
                <ButtonNew
                  type="text"
                  size="small"
                  onClick={() => {
                    setDetailsCard(prev => ({
                      ...prev,
                      internal_description_visible: !prev.internal_description_visible,
                    }));
                  }}
                >
                  {!detailsCard.internal_description_visible ? (
                    <FontAwesomeIcon icon={faPlus} />
                  ) : (
                    <FontAwesomeIcon icon={faMinus} />
                  )}
                  <div style={{ marginLeft: "5px" }}>{t("secure.facility.product.product.096")}</div>
                </ButtonNew>
              </FormLayout.Group>
              {detailsCard?.internal_description_visible ? (
                <FormLayout.Group>
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <TextEditor
                      markdownText={product.internal_description || ""}
                      markdownTextOnChange={internal_description =>
                        setProduct(prev => ({
                          ...prev,
                          internal_description: internal_description === "" ? null : internal_description,
                        }))
                      }
                      label={t("secure.facility.product.product.097")}
                      readOnly={!facilityStore.facility?.permissions.products_edit}
                    />
                    {/* <div className="product-description-subtext">This is a hint text to help the user.</div> */}
                  </div>
                </FormLayout.Group>
              ) : null}
            </FormLayout>
          </Card.Section>

          <Card.Section title={t("secure.facility.product.product_new.036")}>
            <FormLayout>
              <FormLayout.Group>
                <div>
                  <SelectNew
                    id="department_select"
                    label={t("secure.facility.product.product_new.037")}
                    onChange={(value: number) => {
                      setProduct(prev => ({ ...prev, department_id: value }));
                      setDirty(prev => ({ ...prev, department_id: false }));
                    }}
                    error={(!validated("department_id") && attempted) || dirty.department_id}
                    options={
                      departments
                      .filter(value => value.type === "department")
                      .map((department: IDepartments) => ({
                        id: department.id,
                        label: department.title,
                      }))
                    }
                    value={product.department_id}
                  />
                </div>

                <div>
                  <SelectNew
                    id="category_select"
                    label={t("secure.facility.product.product_new.038")}
                    onChange={(value: number) => {
                      setProduct(prev => ({ ...prev, category_id: value }));
                      setDirty(prev => ({ ...prev, category_id: false }));
                    }}
                    error={(!validated("category_id") && attempted) || dirty.category_id}
                    options={
                      departments
                      .filter(value => value.type === "category" && value.parent_id == Number(product.department_id))
                      .map((category: ICategories) => ({
                        id: category.id,
                        label: category.title,
                      }))
                    }
                    value={product.category_id}
                  />
                </div>

                <div>
                  <SelectNew
                    id="subcategory_select"
                    label={t("secure.facility.product.product_new.039")}
                    onChange={(value: number) => {
                      setProduct(prev => ({ ...prev, subcategory_id: value }));
                      setDirty(prev => ({ ...prev, subcategory_id: false }));
                    }}
                    error={(!validated("subcategory_id") && attempted) || dirty.subcategory_id}
                    options={
                      departments
                      .filter(value => value.type === "subcategory" && value.parent_id == Number(product.category_id))
                      .map((subcategory: ISubcategories) => ({
                        id: subcategory.id,
                        label: subcategory.title,
                      }))
                    }
                    value={product.subcategory_id}
                  />
                </div>
              </FormLayout.Group>
            </FormLayout>
          </Card.Section>
        </Card>
      ) : null}

      {/* Pricing & Taxes */}
      {selected === 1 ? (
        <>
          <Card
            title={t("secure.facility.product.product_new.012")}
            titleActions={[
              {
                content: (
                  <Checkbox
                    id="taxable"
                    size="medium"
                    label="Charge Taxes"
                    checked={product.taxable}
                    onChange={() => setProduct(prev => ({ ...prev, taxable: !product.taxable }))}
                  />
                ),
                action: () => {
                  setDetailsCard(prev => ({
                    ...prev,
                    tax_line_id: null,
                  }));
                },
              },
            ]}
          >
            <Card.Section>
              <FormLayout>
                <FormLayout.Group>
                  <Input
                    id="cost"
                    value={comboCostOverride ?? product.cost ?? ""}
                    type="number"
                    prefix="$"
                    label={t("secure.facility.product.product_new.015")}
                    onChange={e => handleCostChange(Number(e.target.value))}
                    placeholder="0.00"
                    error={
                      ((!validated("cost") || (!validated("cost_with_track_inventory") && product.track_inventory)) &&
                        attempted) ||
                      dirty.cost
                    }
                    disabled={comboSelected && (!product.dynamic_combo || variantsCard.multipleOptions)}
                  />
                  <Input
                    id="markup"
                    value={Number(roundNumber(markup, 2)) || ""}
                    onChange={e => handleMarkupChange(Number(e.target.value))}
                    type="number"
                    prefix="%"
                    label={t("Markup")}
                    placeholder="0.00"
                  />
                </FormLayout.Group>
                <FormLayout.Group>
                  <Input
                    id="retail_price"
                    value={comboPriceOverride ?? product.price ?? ""}
                    type="number"
                    prefix="$"
                    label={t("Retail Price")}
                    onChange={e => {
                      handlePriceChange(Number(e.target.value));
                      setDirty(prev => ({ ...prev, price: false }));
                    }}
                    placeholder="0.00"
                    error={(!validated("price") && attempted) || dirty.price}
                    disabled={comboSelected && (!product.dynamic_combo || variantsCard.multipleOptions)}
                  />
                  <div />
                </FormLayout.Group>
              </FormLayout>
            </Card.Section>
            <Card.Section
              title={t("Price after tax")}
              sectionTitleActions={[
                {
                  content: <FontAwesomeIcon icon={hidePriceAfterTax ? faChevronUp : faChevronDown} />,
                  action: () => {
                    setHidePriceAfterTax(!hidePriceAfterTax);
                  },
                },
              ]}
            >
              {!hidePriceAfterTax ? (
                <FormLayout>
                  <FormLayout.Group>
                    <SelectNew
                      id="tax-lines-select"
                      label={t("Tax Lines")}
                      placeholder={getTaxNames() ?? "Select tax lines"}
                      multiValue={selectedTaxLine}
                      options={detailsCard.allTaxLines.map(taxLine => ({
                        id: taxLine.id,
                        label: taxLine.name,
                      }))}
                      onChange={value => toggleTaxLines(value)}
                    />
                    <Input
                      id="price_after_tax"
                      value={getPriceAfterTax() || ""}
                      type={"number"}
                      prefix="$"
                      label={t("Price after tax")}
                      placeholder="0.00"
                      error={!validated("price") && attempted}
                      readOnly={true}
                    />
                  </FormLayout.Group>
                </FormLayout>
              ) : null}
            </Card.Section>
          </Card>
        </>
      ) : null}

      {selected === 2 ? (
        <>
          <Card title={t("secure.facility.product.product_new.026")}>
            <Card.Section>
              <FormLayout>
                <FormLayout.Group>
                  <Select
                    label={t("secure.facility.product.product_new.027")}
                    placeholder={t("secure.facility.product.product_new.028")}
                    showSearch
                    className={`flex justify-center align-center w-full h-10 position-relative z-20 text-black font-medium appearance-none border-none focus:outline-none placeholder-gray-200`}
                    onSearch={(query: string) => handleProductTypeSearch(query)}
                    onChange={(id: number) => {
                      handleSelectProductType(id);
                      setDirty(prev => ({ ...prev, type: false }));
                    }}
                    allowClear
                    searchValue={logisticsCard.productTypeSearch}
                    showDropDownOnFocus
                    hideDropdown
                    error={(!validated("type") && attempted) || dirty.type}
                  >
                    {logisticsCard.types
                      .filter(val =>
                        val.title.toLowerCase().includes(logisticsCard.productTypeSearch.toLocaleLowerCase()),
                      )
                      .map(type => (
                        <Select.Option key={type.id} value={type.id} name={type.title}>
                          <p className="font-semibold text-lg">{type.title}</p>
                        </Select.Option>
                      ))}
                  </Select>

                  <Select
                    label={t("secure.facility.product.product.027")}
                    onChange={(value: number) => {
                      setProduct(prev => ({ ...prev, vendor_id: value }));
                      setDirty(prev => ({ ...prev, vendor_id: false }));
                    }}
                    className={`flex justify-center align-center w-full h-10 position-relative z-20 text-black font-medium appearance-none border-none focus:outline-none placeholder-gray-200`}
                    onSearch={(query: string) => handleVendorSearch(query)}
                    placeholder={t("secure.facility.product.product_new.061")}
                    searchValue={logisticsCard.vendorSearch || ""}
                    error={(!validated("vendor_id") && attempted) || dirty.vendor_id}
                    showSearch
                    showDropDownOnFocus
                    hideDropdown
                    allowClear
                  >
                    {logisticsCard.vendors
                      .filter(val => val.title?.toLowerCase().includes(logisticsCard.vendorSearch?.toLowerCase()))
                      .map((vendor, index: number) => {
                        return (
                          <Select.Option key={index} value={vendor.id} name={vendor.title}>
                            {vendor.title}
                          </Select.Option>
                        );
                      })}
                  </Select>
                </FormLayout.Group>

                {/* Accounting References */}
                <FormLayout.Group>
                  <Select
                    label={t("secure.facility.product.product_new.030")}
                    showSearch
                    className={`flex justify-center align-center w-full h-10 position-relative z-20 text-black font-medium appearance-none border-none focus:outline-none placeholder-gray-200`}
                    onSearch={(query: string) => handleAccountingRefSearch(query)}
                    onChange={(value: number) => {
                      setProduct(prev => ({ ...prev, accounting_reference_id: value }));
                      setDirty(prev => ({ ...prev, accounting_reference_id: false }));
                    }}
                    allowClear
                    placeholder={t("secure.facility.product.product_new.031")}
                    searchValue={logisticsCard.accountingRefSearch}
                    showDropDownOnFocus={true}
                    error={(!validated("accounting_reference_id") && attempted) || dirty.accounting_reference_id}
                  >
                    {logisticsCard.accountingReferences
                      .filter(accountRef =>
                        accountRef.title.toLowerCase().includes(logisticsCard.accountingRefSearch.toLowerCase()),
                      )
                      .map(reference => {
                        return (
                          <div key={reference.id}>
                            {logisticsCard.accountingRefSearch !== "" ? (
                              <div>
                                <Select.Option key={reference.id} value={reference.id} name={reference.title}>
                                  <p className="font-semibold text-lg">{reference.title}</p>
                                </Select.Option>
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                  </Select>
                  <Select
                    label={t("secure.facility.product.product_new.062")}
                    className={`flex justify-center align-center w-full h-10 position-relative z-20 text-black font-medium appearance-none border-none focus:outline-none placeholder-gray-200`}
                    onSearch={(query: string) => handleProductUnitSearch(query)}
                    onChange={(value: number) => setProduct(prev => ({ ...prev, product_unit_id: value }))}
                    allowClear
                    placeholder={t("secure.facility.product.product_new.063")}
                    searchValue={logisticsCard.productUnitSearch}
                    showDropDownOnFocus
                    hideDropdown
                    showSearch
                  >
                    {logisticsCard.productUnits
                      .filter(
                        unit =>
                          unit.name.toLowerCase().includes(logisticsCard.productUnitSearch.toLowerCase()) ||
                          unit.abbreviation?.toLowerCase().includes(logisticsCard.productUnitSearch.toLowerCase()),
                      )
                      .map(unit => {
                        return (
                          <div key={unit.id}>
                            <Select.Option key={unit.id} value={unit.id} name={unit.name}>
                              <p className="font-semibold text-lg">{`${unit.name}${
                                unit.abbreviation ? ` (${unit.abbreviation})` : ""
                              }`}</p>
                            </Select.Option>
                          </div>
                        );
                      })}
                  </Select>
                </FormLayout.Group>
                <FormLayout.Group>
                  <div className="logistics-checkbox-container">
                    <div className="logistics-checkbox-item">
                      <Checkbox
                        id="customer_required"
                        size="medium"
                        value={product.customer_required}
                        checked={Boolean(product?.customer_required)}
                        onChange={e => setProduct(prev => ({ ...prev, customer_required: e.target.checked }))}
                        label={t("secure.facility.product.product_new.033")}
                      />
                    </div>
                    <div className="logistics-checkbox-item">
                      <Checkbox
                        id="shipping_required"
                        size="medium"
                        value={product.shipping_required}
                        checked={Boolean(product?.shipping_required)}
                        onChange={e => setProduct(prev => ({ ...prev, shipping_required: e.target.checked }))}
                        label={t("secure.facility.product.product_new.034")}
                      />
                    </div>
                    <div className="logistics-checkbox-item">
                      <Checkbox
                        id="fulfillment_required"
                        size="medium"
                        value={product.fulfillment_required}
                        checked={Boolean(product.fulfillment_required)}
                        onChange={e => setProduct(prev => ({ ...prev, fulfillment_required: e.target.checked }))}
                        label={t("secure.facility.product.product_new.035")}
                      />
                    </div>
                    <div className="logistics-checkbox-item">
                      <Checkbox
                        id="track_inventory"
                        size="medium"
                        value={product.track_inventory}
                        checked={Boolean(product.track_inventory)}
                        onChange={e => setProduct(prev => ({ ...prev, track_inventory: e.target.checked }))}
                        label={t("secure.facility.product.product_new.064")}
                      />
                    </div>
                    <div className="logistics-checkbox-item">
                      <Checkbox
                        id="warehouse"
                        size="medium"
                        value={product.warehouse}
                        checked={Boolean(product?.warehouse)}
                        onChange={e => setProduct(prev => ({ ...prev, warehouse: e.target.checked }))}
                        label={"Warehouse Inventory Item"}
                      />
                    </div>
                    {product.type === "Gift Card" ? (
                      <div className="logistics-checkbox-item">
                        <Checkbox
                          id="gift_card_restrict_facility"
                          size="medium"
                          value={product.gift_card_restrict_facility}
                          checked={Boolean(product?.gift_card_restrict_facility)}
                          onChange={e =>
                            setProduct(prev => ({ ...prev, gift_card_restrict_facility: e.target.checked }))
                          }
                          label={t("Restrict Gift Card to Facility")}
                        />
                      </div>
                    ) : null}
                  </div>
                </FormLayout.Group>
              </FormLayout>
            </Card.Section>
          </Card>

          <Card title="Inventory" subtitle="Manage your inventory here.">
            <Card.Section>
              <FormLayout>
                <FormLayout.Group>
                  <Input
                    id="barcode"
                    value={product.barcode || ""}
                    onChange={e => handleChangeEventInput(e, product, setProduct)}
                    label={t("secure.facility.product.product.049")}
                    placeholder={t("secure.facility.product.product.049")}
                    //disabled={readOnly || product?.warehouse_product_id !== null}
                  />
                  <span className="flex flex-row items-center gap-4">
                    <div style={{ width: "100%" }}>
                      <Input
                        id="sku"
                        value={product.auto_generate_sku === 0 ? product.sku || "" : ""}
                        onChange={e => handleChangeEventInput(e, product, setProduct)}
                        label={t("secure.facility.product.product.050")}
                        placeholder={
                          product.auto_generate_sku === 0 ? "Enter Sku" : "Sku will be autogenerated upon creation"
                        }
                        //disabled={readOnly || product?.warehouse_product_id !== null}
                        disabled={product.auto_generate_sku === 1}
                        trailingButtons={[
                          <ButtonNew
                            key="generate-sku"
                            onClick={() => {
                              setProduct(prev => ({
                                ...prev,
                                sku: generateSKU(),
                              }));
                            }}
                          >
                            <div className="flex flex-row" style={{ alignItems: "center", gap: "5px", width: "140px" }}>
                              <FontAwesomeIcon icon={faRotate} />
                              <div className="flex flex-row">Generate SKU</div>
                            </div>
                          </ButtonNew>,
                        ]}
                      />
                    </div>
                  </span>
                </FormLayout.Group>
              </FormLayout>
            </Card.Section>
          </Card>
          <ProductMembershipOptions
            isVisible={membershipApplicationFilter !== null}
            applicationFilter={membershipApplicationFilter}
            optionProps={membershipCard}
            setOptionProps={setMembershipCard}
          />
          {renderComboCard()}
        </>
      ) : null}

      {selected === 3 ? (
        <>
          {product.type !== "Modifier" && !product.warehouse ? (
            <Card title={t("secure.facility.product.product_new.021")}>
              <Card.Section>
                <FormLayout>
                  <div className="multiple-options-group">
                    <FormLayout.Group>
                      <Checkbox
                        id="multipleOptions"
                        size="medium"
                        value={variantsCard.multipleOptions}
                        checked={variantsCard.multipleOptions}
                        onChange={e => toggleMultipleOptions(e)}
                        label={t("secure.facility.product.product_new.022")}
                      />
                      <div className="product-new-add-option">
                        <Button
                          onClick={
                            product.options.length < 3
                              ? () =>
                                  setProduct(prev => ({
                                    ...prev,
                                    options: [...prev.options, { name: "", values: [""] }],
                                  }))
                              : undefined
                          }
                          disabled={!variantsCard.multipleOptions || product.options.length > 2}
                        >
                          {t("secure.facility.product.product_new.059")}
                        </Button>
                      </div>
                    </FormLayout.Group>
                  </div>
                </FormLayout>
                {variantsCard.multipleOptions ? (
                  <>
                    {product.options.map((option, index: number) => {
                      return (
                        <Card.SubSection key={index}>
                          <FormLayout>
                            {/** FormLayout.Group styling + flex-grow for TagInput */}
                            <div className="multiple-options_spacing">
                              <Input
                                id={`[options][${index}][name]`}
                                value={option.name || ""}
                                label={`${t("secure.facility.product.product_new.054")} ${index + 1}`}
                                onChange={handleOptionChange}
                                placeholder={t("secure.facility.product.product_new.023")}
                              />

                              <TagInput
                                label={"Values"}
                                id={`[options][${index}][values][${
                                  option.values.length === 0 ? 0 : option.values.length - 1
                                }]`}
                                value={option.values[option.values.length === 0 ? 0 : option.values.length - 1]}
                                onChange={handleOptionChange}
                                onKeyDown={handleAddOptionTag}
                                placeholder={t("secure.facility.product.product_new.025")}
                                tags={option.values.slice(0, option.values.length - 1)}
                                onTagClick={(tagIndex: number) => handleRemoveOptionTag(index, tagIndex)}
                                helpText={t("secure.facility.product.product_new.070")}
                              />

                              <FontAwesomeIcon
                                className="ml-auto block mt-8 cursor-pointer"
                                icon={["far", "x"]}
                                onClick={() => removeVariantOption(index)}
                              />
                            </div>
                          </FormLayout>
                        </Card.SubSection>
                      );
                    })}
                  </>
                ) : null}
              </Card.Section>
            </Card>
          ) : null}
        </>
      ) : null}

      {selected === 4 ? (
        <Page.Section>
          {/* SALES CHANNELS & Dates */}
          <Card title={t("secure.facility.product.product.057")}>
            <Card.Section>
              <FormLayout>
                <FormLayout.Group>
                  {salesChannel.map(channel => (
                    <Toggle
                      key={channel.id}
                      id={String(channel.id)}
                      value={channel.facility_id}
                      labelTop={channel.title}
                      onChange={() =>
                        setSalesChannel(prev =>
                          prev.map(item => (item.id === channel.id ? { ...item, checked: !item.checked } : item)),
                        )
                      }
                      //disabled={!authStore.user?.permissions?.products_edit}
                    />
                  ))}
                </FormLayout.Group>
                {salesChannel.find(channel => channel.title.toLowerCase() === "online store").checked && (
                  <FormLayout.Group>
                    <CopyLink
                      label="Online Store"
                      link={
                        window.origin +
                        "/online-store/" +
                        facilityStore?.facility?.short_name +
                        "/product/" +
                        String(product.title.toLowerCase().replace(/\s+/g, "-"))
                      }
                    />

                    {/* <button className="flex gap-3 justify-end w-full" onClick={moveToProductOnlineStore}>
                    <span className="edit-product-view-online-store-text">View in online store</span>
                    <FontAwesomeIcon
                      className="edit-product-view-online-store-icon"
                      icon={["far", "arrow-up-right-from-square"]}
                    />
                  </button> */}
                  </FormLayout.Group>
                )}
              </FormLayout>
            </Card.Section>
            <Card.Section>
              <FormLayout>
                <FormLayout.Group>
                  <Toggle
                    id="available_on_toggle"
                    labelRight={t("secure.facility.product.product.058")}
                    onChange={handleAvailableOnToggle}
                    checked={availableAtDate !== null}
                    //disabled={readOnly}
                  />
                </FormLayout.Group>
                <FormLayout.Group>
                  {availableAtDate === null ? null : (
                    <span className="flex flex-row items-center gap-4">
                      <div style={{ width: "100%" }}>
                        <DatePickerInput
                          months={1}
                          position={"left"}
                          startingDate={availableAtDate}
                          label={t("secure.facility.product.product.059")}
                          setStartingDate={(selectedDate: any) => {
                            //if (authStore.user?.permissions?.products_edit) {
                            handleAvailableOnDateSelectorChange(selectedDate as Date);
                            //}
                          }}
                          //error={!availableOnDateIsValid()}
                        />
                      </div>

                      <div style={{ display: "flex", marginTop: "15px", width: "100%", alignItems: "center" }}>
                        <TimePick
                          value={product.available_on_time}
                          onChange={timeString => handleTimeAvailability("available_on_time", timeString)}
                          //disabled={readOnly}
                          align="right"
                          //status={!availableOnDateIsValid() ? "error" : undefined}
                          size="large"
                        />
                        <label style={{ marginLeft: "5px", marginBottom: "0px" }} htmlFor="">
                          {moment().tz(Intl.DateTimeFormat().resolvedOptions().timeZone).zoneAbbr()}
                        </label>
                      </div>
                    </span>
                  )}
                </FormLayout.Group>

                <FormLayout.Group>
                  <Toggle
                    id="unavailable_on_toggle"
                    labelRight={t("secure.facility.product.product.060")}
                    onChange={handleAvailableUntilToggle}
                    checked={availableUntilDate !== null}
                    //disabled={readOnly}
                  />
                </FormLayout.Group>
                <FormLayout.Group>
                  {availableUntilDate === null ? null : (
                    <span className="flex flex-row items-center gap-4">
                      <div style={{ width: "100%" }}>
                        <DatePickerInput
                          months={1}
                          position={"left"}
                          startingDate={availableUntilDate}
                          label={t("secure.facility.product.product.061")}
                          setStartingDate={(selectedDate: any) => {
                            //if (authStore.user?.permissions?.products_edit) {
                            handleAvailableUntilDateSelectorChange(selectedDate as Date);
                            //}
                          }}
                          //error={!availableUntilDateIsValid()}
                        />
                      </div>
                      <div style={{ display: "flex", marginTop: "15px", width: "100%", alignItems: "center" }}>
                        <TimePick
                          value={product.unavailable_on_time}
                          onChange={timeString => handleTimeAvailability("unavailable_on_time", timeString)}
                          //disabled={readOnly}
                          align="right"
                          //status={!availableUntilDateIsValid() ? "error" : undefined}
                          size="large"
                        />
                        <label style={{ marginLeft: "5px", marginBottom: "0px" }} htmlFor="">
                          {moment().tz(Intl.DateTimeFormat().resolvedOptions().timeZone).zoneAbbr()}
                        </label>
                      </div>
                    </span>
                  )}
                </FormLayout.Group>
              </FormLayout>
            </Card.Section>
          </Card>
        </Page.Section>
      ) : null}

      {/* IMAGES */}
      {selected === 5 ? (
        <Page.Section>
          <Card>
            <Card.Section title={t("Media")}>
              <ImageUpload
                image={product?.image}
                onNew={img => {
                  setImageURL(URL.createObjectURL(img));
                  setProduct(prev => ({
                    ...prev,
                    image: {
                      ...prev.image,
                      source: img,
                    },
                  }));
                  setImageFile(img);
                }}
                onRemove={() => {
                  setImageURL(null);
                  setProduct(prev => ({ ...prev, image: [] }));
                  setImageFile(null);
                }}
                minHeight={200}
                minWidth={200}
                imageURL={imageURL}
              />
            </Card.Section>

            {/* COLOR */}
            <Card.Section title={t("Colour")}>
              <ColorPicker
                colorValue={product?.meta?.button_color}
                onClick={(codeColor: string) => {
                  setProduct(prev => ({
                    ...prev,
                    meta: {
                      ...prev.meta,
                      button_color: codeColor,
                    },
                  }));
                }}
              />
            </Card.Section>
          </Card>
        </Page.Section>
      ) : null}

      <Portal isMounted={discountModal.isOpen}>
        <Sheet
          open={discountModal.isOpen}
          title={t("New Discount")}
          size="medium"
          closable
          onCancel={() => closeDiscountModal()}
          onOk={() => {
            void addDiscount(discountModal);
            closeDiscountModal();
          }}
          cancelText={t("secure.facility.product.product_new.041")}
          okText={t("Confirm")}
          backDropCancel={false}
        >
          <Input
            label={t("secure.facility.product.product_new.011")}
            value={discountModal.title}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateDiscountModal({ title: e.target.value })}
            type="text"
            id={"description"}
            placeholder={t("Discount name or description")}
          />
          <Card.Section>
            <span className="flex flex-row gap-4 mt-3">
              <SelectNew
                id="defaultDiscount"
                label={t("Default Discount")}
                placeholder={t("Type")}
                value={discountModal.value_type}
                options={[
                  {
                    label: t("Above Cost"),
                    id: "0",
                  },
                  {
                    label: t("Below Retail"),
                    id: "1",
                  },
                ]}
                onChange={value => updateDiscountModal({ value_type: value })}
                className="w-1/2"
              />
              <div className="w-1/2 mt-1.5">
                <Input
                  label="Value (%)"
                  value={discountModal.value}
                  onChange={e => updateDiscountModal({ value: Number(e.target.value) })}
                  id={"value"}
                  placeholder="0"
                />
              </div>
            </span>
          </Card.Section>
          <br />
          <Card.Section className="mt-4">
            <Checkbox
              id="scheduledDiscount"
              size="medium"
              checked={discountModal.scheduledDiscount}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateDiscountModal({ scheduledDiscount: e.target.checked })
              }
              label={t("Scheduled Discount")}
            />
          </Card.Section>
          <br />
          {discountModal.scheduledDiscount ? (
            <Card.Section>
              <div className="flex flex-row gap-4">
                <div className="w-1/2">
                  <DatePickerInput
                    label="Schedule Discount"
                    months={2}
                    position="left"
                    startingDate={discountModal.start_date}
                    setStartingDate={setDateRangeStart}
                    endingDate={discountModal.end_date}
                    setEndingDate={setDateRangeEnd}
                    //multipleDates={discountModal.datesRange}
                    disablePastDays
                  />
                </div>
                <div className="w-1/4" style={{ marginTop: "0.5rem" }}>
                  {/* <Input
                      label="Start Time"
                      value={discountModal.startTime}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        updateDiscountModal({ startTime: e.target.value })
                      }
                      id={"startTime"}
                      type="time"
                    /> */}
                  <TimePick
                    label="Start Time"
                    value={discountModal.startTime}
                    onChange={value => updateDiscountModal({ startTime: value })}
                  />
                </div>
                <div className="w-1/4" style={{ marginTop: "0.5rem" }}>
                  {/* <Input
                      label="End Time"
                      value={discountModal.endTime}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        updateDiscountModal({ endTime: e.target.value })
                      }
                      id={"endTime"}
                      type="time"
                    /> */}
                  <TimePick
                    label="End Time"
                    value={discountModal.endTime}
                    onChange={value => updateDiscountModal({ startTime: value })}
                  />
                </div>
              </div>
              <br />
            </Card.Section>
          ) : null}
          <SelectNew
            label="Sales Channel"
            value={discountModal.salesChannel}
            onChange={value => updateDiscountModal({ salesChannel: value })}
            options={salesChannel.map((channel: any) => {
              return { label: channel.title, id: channel.id };
            })}
            id={"salesChannel"}
            placeholder={t("Find sales channel")}
          />
          <br />
          <SelectNew
            label="Customer Type"
            multiValue={selectedCustomerType}
            onChange={value => toggleCustomerType(value)}
            options={customerTypes.map((type: any) => {
              return { label: type.title, id: type.id };
            })}
            id={"customerType"}
            placeholder={t("Find customer type")}
          />
          <br />
          <DataTable
            columns={[
              {
                label: " ",
                content: (
                  <Checkbox
                    id="selectAllDiscounts"
                    size="medium"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleAllCustomerTypeCheckbox(e)}
                  />
                ),
                width: "5%",
              },
              { label: t("secure.facility.product.product_new.048"), width: "90%" },
              {
                label: t("Remove all"),
                content: (
                  <div
                    onClick={() => setSelectedCustomerType([])}
                    style={{ fontSize: "12px", fontWeight: 500, color: "#475467", cursor: "pointer" }}
                  >
                    Remove all
                  </div>
                ),
                width: "5%",
              },
            ]}
          >
            {selectedCustomerType.map((type: any, index: number) => {
              return (
                <tr key={type.id}>
                  <td>
                    <Checkbox
                      id={type}
                      size="medium"
                      value={discountModal.customerType || false}
                      //checked={Array.isArray(discountModal.customerType) ? discountModal.customerType.includes(type) : false}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCustomerTypeCheckbox(e, index)}
                    />
                  </td>
                  <td>{customerTypes.find(customerType => customerType.id === type).title}</td>
                  <td>
                    <div
                      id={type}
                      className="flex gap-2 hover:cursor-pointer justify-center"
                      onClick={() => {
                        toggleCustomerType(type as number);
                      }}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </DataTable>
        </Sheet>
      </Portal>

      <Portal isMounted={newComboVariantModal.isOpen}>
        <AttachProductAsComboModal
          isVisible={newComboVariantModal.isOpen}
          currentlyAddedProducts={comboState.comboVariants}
          userLevel={"facility"}
          onCancel={closeNewComboVariantModal}
          onOk={(variantId, variant: IVariant, product: IProduct) => handleNewComboVariantModalOk(variant, product)}
          useOkAsCancel
        />
      </Portal>

      <Portal isMounted={editComboVariantModal.isOpen}>
        <Sheet
          open={editComboVariantModal.isOpen}
          title={editComboVariantModal.selectedComboVariant?.variant_title}
          size="small"
          closable
          onCancel={closeEditComboVariantModal}
          backDropCancel={false}
          onOk={handleEditComboVariantModalOk}
          okDisabled={!editComboVariantModal.priceIsValid}
        >
          <FormLayout>
            <FormLayout.Group>
              <Input
                label={t("secure.facility.product.new_product.021")}
                value={editComboVariantModal.selectedComboVariant?.price || ""}
                onChange={e => handleEditComboVariantPrice(e.target.value)}
                type={"number"}
                placeholder="0.00"
                prefix="$"
                error={!editComboVariantModal.priceIsValid}
              />

              <Input
                label={t("secure.facility.product.new_product.022")}
                value={editComboVariantModal.selectedComboVariant?.cost || ""}
                onChange={e => handleEditComboVariantCost(e.target.value)}
                type={"number"}
                placeholder="0.00"
                prefix="$"
              />
            </FormLayout.Group>
          </FormLayout>
        </Sheet>
      </Portal>
    </Page>
  );
}

const removeEmpty = (product: TPostProduct, multipleOptions: boolean): TPostProduct => {
  //remove blank options
  let newObj: TPostProduct = defaultPostProduct;

  Object.keys(product).forEach((key: keyof TPostProduct) => {
    const value = product[key];

    if (key !== "options") {
      if (typeof value === "string" && value.trim() === "") {
        // Remove empty strings
        newObj = {
          ...newObj,
          [key]: null as string,
        };
      } else if (typeof value === "number" && value === -1) {
        // -1 select values set to null
        newObj = {
          ...newObj,
          [key]: null as number,
        };
      } else {
        newObj = {
          ...newObj,
          [key]: value,
        };
      }
    }
    if (key === "meta") {
      newObj[key] = {
        ...newObj[key],
        membership_type: newObj[key].membership_type.trim() === "" ? null : newObj[key].membership_type,
        customer_type_id: newObj[key].customer_type_id === -1 ? null : newObj[key].customer_type_id,
        expiry: newObj[key].expiry,
      };
    }
  });

  if (!multipleOptions) {
    return { ...newObj, options: [] };
  } else {
    const tempOptions = [...product.options];

    tempOptions.forEach((option, index) => {
      if (option.name.trim() === "") {
        tempOptions.splice(index, 1);
      } else {
        option.values.forEach((tag, tagIndex) => {
          if (tag.trim() === "") {
            tempOptions[index].values.splice(tagIndex, 1);
          }
        });
      }
    });

    return { ...newObj, options: tempOptions };
  }
};
